import React, { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EntityTypes, OPPORTUNITIES, COMPANIES, SALESORDERS } from 'constants/Entities';
import VirtualizedList from 'components/VirtualizedList';
import DrilldownItem from 'components/drilldown/DrilldownItem';
import DrilldownHeader from 'components/drilldown/DrilldownHeader';
import { PATH_IMAGE } from 'constants/Environment';
import { getLiteral } from 'utils/getLiteral';
import { getDetailRoute } from 'utils/fm';
import { getEnvironmentRoute } from 'utils/routes';
import { formatDateHourToday, getSectionTime } from 'utils/dates';
import { formatLargeAmount } from 'utils/amount';
import { getNumberAsLocaleNumber } from 'utils/numbers';
import LoaderHoi from 'components/LoaderHoi';

import './styles.scss';
import { KpisActions } from 'actions';

const LIMIT = 100;

const TabDrilldown = memo(({ data }) => {
    const dispatch = useDispatch();
    const currency = useSelector((state) => state.config.userData.currencySymbol || '');

    const [initialLoading, setInitialLoading] = useState(true);
    const [flatItems, setFlatItems] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const lastHeaderRef = useRef(null);
    const isLoadingRef = useRef(false);

    const entityToClick = useMemo(
        () => ({
            [1]: (id) => getDetailRoute({ entity: OPPORTUNITIES, id }),
            [2]: (id) => getDetailRoute({ entity: COMPANIES, id }),
            [55]: (id) =>
                getEnvironmentRoute(
                    '/sales-orders',
                    `?entity=${SALESORDERS.entity}&action=detail&id=${id}`,
                ),
        }),
        [],
    );

    const processItems = useCallback(
        (rawItems) => {
            return rawItems.map((item) => {
                let fields = [];
                let dateTime;

                for (let i = 1; i <= 4; i++) {
                    let value = item[`value${i}`];
                    const type = item[`type${i}`];

                    if (value && type) {
                        switch (type) {
                            case 'text':
                                value = String(value);
                                break;
                            case 'dateTime':
                            case 'date':
                                value = formatDateHourToday(value, 'YYYY-MM-DD HH:mm:ss', 'L LT');
                                break;
                            case 'currency':
                                value = formatLargeAmount(value, currency);
                                break;
                            case 'float':
                                value = getNumberAsLocaleNumber(value);
                                break;
                            default:
                                break;
                        }

                        if (type === 'dateTime' || (type === 'date' && !dateTime)) {
                            dateTime = {
                                value: item[`value${i}`],
                                type: item[`type${i}`],
                                name: item[`name${i}`],
                            };
                        }
                    }
                    fields.push({
                        value,
                        type,
                        name: item[`name${i}`],
                    });
                }

                const entity = EntityTypes[item.objectTypeId];
                let sectionHeader;
                if (dateTime) {
                    sectionHeader = getSectionTime(dateTime.value, 'YYYY-MM-DD HH:mm:ss');
                }

                const isClickable = !!entityToClick[item.objectTypeId];

                return {
                    header: sectionHeader ? getLiteral(sectionHeader) : null,
                    id: item.entityId,
                    type: item.objectTypeId,
                    title: fields[0].value,
                    subtitle: fields[2].value,
                    rightTopField: fields[1].value,
                    rightBottomField: fields[3].value,
                    icon: entity?.icon || 'entity-icon icon-kpis',
                    isClickable,
                };
            });
        },
        [currency, entityToClick],
    );

    const flattenWithHeaders = useCallback((items, resetHeader = false) => {
        const result = [];

        if (resetHeader) {
            lastHeaderRef.current = null;
        }

        items.forEach((item) => {
            const header = item.header || null;

            if (header !== lastHeaderRef.current) {
                if (header) {
                    result.push({ type: 'header', title: header });
                }
                lastHeaderRef.current = header;
            }

            result.push({ type: 'item', data: item });
        });

        return result;
    }, []);

    const onClick = useCallback(
        (item) => {
            const toClick = entityToClick[item.type];
            if (toClick) window.location.href = toClick(item.id);
        },
        [entityToClick],
    );

    const loadMoreItems = useCallback(
        ({ resetOffset } = {}) => {
            if (resetOffset) {
                return Promise.resolve([]);
            }

            if (!hasMore || isLoadingRef.current) {
                return Promise.resolve([]);
            }

            isLoadingRef.current = true;
            const currentOffset = offsetRef.current;

            return dispatch(KpisActions.getGoalDrilldown(data.id, currentOffset, LIMIT))
                .then((newItems) => {
                    if (!newItems || newItems.length === 0) {
                        setHasMore(false);
                        isLoadingRef.current = false;
                        return [];
                    }

                    const processedItems = processItems(newItems);
                    const itemsWithHeaders = flattenWithHeaders(processedItems, false);

                    setFlatItems((prev) => [...prev, ...itemsWithHeaders]);

                    offsetRef.current = currentOffset + newItems.length;

                    if (newItems.length < LIMIT) {
                        setHasMore(false);
                    }

                    isLoadingRef.current = false;
                    return newItems;
                })
                .catch(() => {
                    setHasMore(false);
                    isLoadingRef.current = false;
                    return [];
                });
        },
        [data.id, dispatch, flattenWithHeaders, hasMore, processItems],
    );

    const renderRow = useCallback(
        ({ data: item, index }) => {
            if (!item) return null;

            if (item.type === 'header') {
                return (
                    <div key={`header-${index}`} className="fm-drilldown-header-wrapper">
                        <DrilldownHeader title={item.title} />
                    </div>
                );
            }

            if (item.type === 'item') {
                const itemData = item.data;
                return (
                    <DrilldownItem
                        key={`item-${itemData.id}-${index}`}
                        onClick={() => onClick(itemData)}
                        {...itemData}
                    />
                );
            }

            return null;
        },
        [onClick],
    );

    useEffect(() => {
        if (!data?.id || !data?.hasDrilldown) {
            setInitialLoading(false);
            setFlatItems([]);
            return;
        }

        setInitialLoading(true);
        offsetRef.current = 0;
        lastHeaderRef.current = null;
        isLoadingRef.current = false;
        setFlatItems([]);
        setHasMore(true);

        dispatch(KpisActions.getGoalDrilldown(data.id, 0, LIMIT))
            .then((items) => {
                if (!items || items.length === 0) {
                    setFlatItems([]);
                    setHasMore(false);
                    setInitialLoading(false);
                } else {
                    const processedItems = processItems(items);
                    const itemsWithHeaders = flattenWithHeaders(processedItems, true);
                    setFlatItems(itemsWithHeaders);
                    offsetRef.current = items.length;

                    if (items.length < LIMIT) {
                        setHasMore(false);
                    }
                    setInitialLoading(false);
                }
            })
            .catch(() => {
                setFlatItems([]);
                setInitialLoading(false);
                setHasMore(false);
            });
    }, [data?.id, data?.hasDrilldown, dispatch, flattenWithHeaders, processItems]);

    const showLoading = initialLoading;
    const showEmpty =
        !data?.hasDrilldown || (flatItems.length === 0 && !hasMore && !initialLoading);
    const showList = flatItems.length > 0 && !initialLoading;

    const noDataLiteral =
        data.hasDrilldown && !data.valuePerformed
            ? 'label_goals_drilldown_no_activity'
            : 'label_goals_drilldown_no_data';

    return (
        <div className="goals-detail-content__drilldown">
            {showLoading && (
                <div className="goals-detail-content__drilldown-loading">
                    <LoaderHoi size={'big'} />
                </div>
            )}
            {showEmpty && (
                <div className="goals-detail-content__drilldown-empty">
                    <img className="empty-image" src={`${PATH_IMAGE}img-empty-kpi.svg`} />
                    <div className="empty-text">{getLiteral(noDataLiteral)}</div>
                </div>
            )}
            {showList && (
                <VirtualizedList
                    data={flatItems}
                    total={hasMore ? flatItems.length + 1 : flatItems.length}
                    renderRow={renderRow}
                    onLoad={loadMoreItems}
                    overscanCount={5}
                />
            )}
        </div>
    );
});

export default TabDrilldown;
